import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Gauge,
  Activity,
  Users,
  AlertTriangle,
  Clock,
  Cpu,
  MemoryStick,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { usePerformanceMetrics } from "@/hooks/admin/useEnterprise";
import type { PerformanceMetric, ApiEndpointStats, MetricStatus } from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const METRIC_STATUS_COLORS: Record<MetricStatus, string> = {
  ok: "text-emerald-700",
  warning: "text-amber-700",
  critical: "text-red-700",
};

const METRIC_STATUS_BG: Record<MetricStatus, string> = {
  ok: "bg-emerald-50 border-emerald-200",
  warning: "bg-amber-50 border-amber-200",
  critical: "bg-red-50 border-red-200",
};

const METRIC_STATUS_BAR: Record<MetricStatus, string> = {
  ok: "bg-emerald-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
};

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-blue-100 text-blue-800 border-blue-200",
  POST: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PATCH: "bg-amber-100 text-amber-800 border-amber-200",
  PUT: "bg-amber-100 text-amber-800 border-amber-200",
  DELETE: "bg-red-100 text-red-800 border-red-200",
};

const TIME_RANGES: readonly { readonly value: string; readonly label: string }[] = [
  { value: "1h", label: "1h" },
  { value: "6h", label: "6h" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7j" },
  { value: "30d", label: "30j" },
];

// ---------------------------------------------------------------------------
// Mock chart data generation
// ---------------------------------------------------------------------------

function generateChartData(range: string): readonly {
  readonly time: string;
  readonly responseTime: number;
  readonly errorRate: number;
  readonly activeUsers: number;
}[] {
  const points =
    range === "1h" ? 12 : range === "6h" ? 24 : range === "24h" ? 48 : range === "7d" ? 28 : 30;

  const now = Date.now();
  const intervalMs =
    range === "1h"
      ? 5 * 60 * 1000
      : range === "6h"
      ? 15 * 60 * 1000
      : range === "24h"
      ? 30 * 60 * 1000
      : range === "7d"
      ? 6 * 60 * 60 * 1000
      : 24 * 60 * 60 * 1000;

  return Array.from({ length: points }, (_, i) => {
    const timestamp = now - (points - 1 - i) * intervalMs;
    const date = new Date(timestamp);
    const timeLabel =
      range === "7d" || range === "30d"
        ? date.toLocaleDateString("fr-SN", { day: "2-digit", month: "2-digit" })
        : date.toLocaleTimeString("fr-SN", { hour: "2-digit", minute: "2-digit" });

    return {
      time: timeLabel,
      responseTime: Math.round(120 + Math.random() * 180 + Math.sin(i * 0.5) * 40),
      errorRate: Math.max(0, +(0.5 + Math.random() * 2.5 + Math.sin(i * 0.3) * 0.8).toFixed(2)),
      activeUsers: Math.round(45 + Math.random() * 80 + Math.sin(i * 0.4) * 20),
    };
  });
}

// ---------------------------------------------------------------------------
// Metric Card
// ---------------------------------------------------------------------------

interface MetricCardProps {
  readonly metric: PerformanceMetric;
}

function MetricCard({ metric }: MetricCardProps) {
  const percentage = Math.min(100, Math.round((metric.value / metric.threshold) * 100));

  return (
    <div className={`rounded-lg border p-4 ${METRIC_STATUS_BG[metric.status]}`}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-stone-600">{metric.name}</p>
        <span className={`text-xs font-semibold uppercase ${METRIC_STATUS_COLORS[metric.status]}`}>
          {metric.status === "ok" ? "OK" : metric.status === "warning" ? "Attention" : "Critique"}
        </span>
      </div>
      <p className={`mt-1.5 text-2xl font-bold ${METRIC_STATUS_COLORS[metric.status]}`}>
        {metric.value.toLocaleString("fr-SN")}
        <span className="ml-1 text-sm font-normal text-stone-500">{metric.unit}</span>
      </p>
      <div className="mt-2 h-1.5 rounded-full bg-stone-200">
        <div
          className={`h-1.5 rounded-full transition-all ${METRIC_STATUS_BAR[metric.status]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-stone-500">
        Seuil : {metric.threshold.toLocaleString("fr-SN")} {metric.unit}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Uptime Display
// ---------------------------------------------------------------------------

function UptimeDisplay({ value }: { readonly value: number }) {
  const color =
    value >= 99.9 ? "text-emerald-600" : value >= 99 ? "text-amber-600" : "text-red-600";

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-stone-200 bg-white p-6">
      <p className="text-xs font-medium text-stone-500">Disponibilite (Uptime)</p>
      <p className={`mt-2 font-display text-4xl font-bold ${color}`}>
        {value.toFixed(2)}%
      </p>
      <div className="mt-3 flex items-center gap-1.5">
        <Activity className={`h-4 w-4 ${color}`} />
        <span className={`text-sm font-medium ${color}`}>
          {value >= 99.9 ? "Operationnel" : value >= 99 ? "Degrade" : "Incident"}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resource Usage Bar
// ---------------------------------------------------------------------------

interface ResourceBarProps {
  readonly label: string;
  readonly value: number;
  readonly icon: typeof Cpu;
}

function ResourceBar({ label, value, icon: Icon }: ResourceBarProps) {
  const color =
    value >= 90 ? "bg-red-500" : value >= 70 ? "bg-amber-500" : "bg-emerald-500";
  const textColor =
    value >= 90 ? "text-red-700" : value >= 70 ? "text-amber-700" : "text-emerald-700";

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-stone-400" />
          <span className="text-sm font-medium text-stone-700">{label}</span>
        </div>
        <span className={`text-sm font-bold ${textColor}`}>{value}%</span>
      </div>
      <div className="mt-2 h-2.5 w-full rounded-full bg-stone-200">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Endpoint table columns
// ---------------------------------------------------------------------------

function buildEndpointColumns(): readonly ColumnDef<ApiEndpointStats>[] {
  return [
    {
      id: "method",
      header: "Methode",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded border px-2 py-0.5 font-mono text-xs font-semibold ${
            METHOD_COLORS[row.method] ?? "bg-stone-100 text-stone-700 border-stone-200"
          }`}
        >
          {row.method}
        </span>
      ),
    },
    {
      id: "path",
      header: "Chemin",
      cell: (row) => (
        <code className="text-xs text-stone-700">{row.path}</code>
      ),
    },
    {
      id: "avg_response_ms",
      header: "Moy. (ms)",
      cell: (row) => {
        const color =
          row.avg_response_ms > 500
            ? "text-red-600"
            : row.avg_response_ms > 200
            ? "text-amber-600"
            : "text-emerald-600";
        return <span className={`text-sm font-medium ${color}`}>{row.avg_response_ms}</span>;
      },
    },
    {
      id: "p95_ms",
      header: "p95 (ms)",
      cell: (row) => {
        const color =
          row.p95_ms > 1000
            ? "text-red-600"
            : row.p95_ms > 500
            ? "text-amber-600"
            : "text-stone-700";
        return <span className={`text-sm ${color}`}>{row.p95_ms}</span>;
      },
    },
    {
      id: "error_rate_percent",
      header: "Erreurs %",
      cell: (row) => (
        <span
          className={`text-sm font-medium ${
            row.error_rate_percent > 5
              ? "text-red-600"
              : row.error_rate_percent > 1
              ? "text-amber-600"
              : "text-emerald-600"
          }`}
        >
          {row.error_rate_percent.toFixed(2)} %
        </span>
      ),
    },
    {
      id: "calls_per_hour",
      header: "Appels/h",
      cell: (row) => (
        <span className="text-sm text-stone-600">
          {row.calls_per_hour.toLocaleString("fr-SN")}
        </span>
      ),
    },
  ];
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Performance() {
  useAdminPageTitle("Performances");

  const { data, isLoading, isError, dataUpdatedAt, refetch } = usePerformanceMetrics();
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeRange, setTimeRange] = useState("24h");

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      refetch();
    }, 60_000);
    return () => clearInterval(interval);
  }, [autoRefresh, refetch]);

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt).toLocaleTimeString("fr-SN"));
    }
  }, [dataUpdatedAt]);

  const handleToggleAutoRefresh = useCallback(() => {
    setAutoRefresh((prev) => {
      const next = !prev;
      toast.success(next ? "Actualisation automatique activee" : "Actualisation automatique desactivee");
      return next;
    });
  }, []);

  const handleManualRefresh = useCallback(() => {
    refetch();
    toast.success("Donnees actualisees");
  }, [refetch]);

  const metrics = data?.metrics ?? [];
  const topEndpoints = data?.top_endpoints ?? [];
  const slowEndpoints = data?.slow_endpoints ?? [];

  const endpointColumns = useMemo(() => buildEndpointColumns(), []);
  const chartData = useMemo(() => generateChartData(timeRange), [timeRange]);

  // Simulated system metrics
  const uptimePercent = 99.95;
  const cpuUsage = 42;
  const memoryUsage = 68;

  // KPI values from metrics
  const avgResponseTime = useMemo(
    () => metrics.find((m) => m.name.toLowerCase().includes("reponse"))?.value ?? 156,
    [metrics],
  );
  const errorRate = useMemo(
    () => metrics.find((m) => m.name.toLowerCase().includes("erreur"))?.value ?? 0.34,
    [metrics],
  );
  const activeUsers = useMemo(
    () => metrics.find((m) => m.name.toLowerCase().includes("utilisateur"))?.value ?? 127,
    [metrics],
  );

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-10 w-10 text-red-400" />
          <p className="mt-3 text-sm font-medium text-stone-700">
            Erreur lors du chargement des metriques
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Veuillez reessayer ou contacter le support.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Gauge className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Performances
              </h1>
              <p className="text-sm text-stone-500">
                Tableau de bord de monitoring systeme
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Time range selector */}
            <div className="flex rounded-lg border border-stone-200 bg-white p-0.5">
              {TIME_RANGES.map((range) => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    timeRange === range.value
                      ? "bg-amber-100 text-amber-900"
                      : "text-stone-600 hover:bg-stone-50"
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>

            {/* Auto-refresh toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleManualRefresh}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-stone-200 text-stone-500 transition-colors hover:bg-stone-50"
                title="Actualiser"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button
                onClick={handleToggleAutoRefresh}
                className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                  autoRefresh
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-stone-200 bg-white text-stone-500"
                }`}
              >
                <Activity className={`h-3 w-3 ${autoRefresh ? "animate-pulse" : ""}`} />
                Auto
              </button>
            </div>

            {/* Last updated */}
            <div className="text-right">
              {lastUpdated && (
                <p className="text-xs text-stone-400">MAJ : {lastUpdated}</p>
              )}
            </div>
          </div>
        </div>

        {/* Top KPI cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Uptime"
            value={`${uptimePercent}%`}
            icon={<Activity className="h-4 w-4" />}
          />
          <KPICard
            label="Temps de reponse moyen"
            value={`${avgResponseTime} ms`}
            icon={<Clock className="h-4 w-4" />}
          />
          <KPICard
            label="Taux d'erreur"
            value={`${errorRate}%`}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <KPICard
            label="Utilisateurs actifs"
            value={activeUsers}
            icon={<Users className="h-4 w-4" />}
          />
        </div>

        {/* Uptime + resource usage */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <UptimeDisplay value={uptimePercent} />
          <ResourceBar label="CPU" value={cpuUsage} icon={Cpu} />
          <ResourceBar label="Memoire" value={memoryUsage} icon={MemoryStick} />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Response time chart */}
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <h3 className="mb-4 text-sm font-semibold text-stone-900">
              Temps de reponse (ms)
            </h3>
            {isLoading ? (
              <div className="flex h-52 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-stone-200 border-t-amber-500" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData as unknown as Record<string, unknown>[]}>
                  <defs>
                    <linearGradient id="responseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4A535" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#D4A535" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 11, fill: "#78716c" }}
                    tickLine={false}
                    axisLine={{ stroke: "#d6d3d1" }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#78716c" }}
                    tickLine={false}
                    axisLine={{ stroke: "#d6d3d1" }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fafaf9",
                      border: "1px solid #e7e5e4",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    formatter={(value: number) => [`${value} ms`, "Temps de reponse"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="responseTime"
                    stroke="#D4A535"
                    strokeWidth={2}
                    fill="url(#responseGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Error rate chart */}
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <h3 className="mb-4 text-sm font-semibold text-stone-900">
              Taux d'erreur (%)
            </h3>
            {isLoading ? (
              <div className="flex h-52 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-stone-200 border-t-amber-500" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData as unknown as Record<string, unknown>[]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 11, fill: "#78716c" }}
                    tickLine={false}
                    axisLine={{ stroke: "#d6d3d1" }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#78716c" }}
                    tickLine={false}
                    axisLine={{ stroke: "#d6d3d1" }}
                    domain={[0, "auto"]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fafaf9",
                      border: "1px solid #e7e5e4",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    formatter={(value: number) => [`${value}%`, "Taux d'erreur"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="errorRate"
                    stroke="#C75B39"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#C75B39" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Active users chart */}
        <div className="rounded-lg border border-stone-200 bg-white p-5">
          <h3 className="mb-4 text-sm font-semibold text-stone-900">
            Utilisateurs actifs
          </h3>
          {isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-stone-200 border-t-amber-500" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData as unknown as Record<string, unknown>[]}>
                <defs>
                  <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C75B39" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#C75B39" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 11, fill: "#78716c" }}
                  tickLine={false}
                  axisLine={{ stroke: "#d6d3d1" }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#78716c" }}
                  tickLine={false}
                  axisLine={{ stroke: "#d6d3d1" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fafaf9",
                    border: "1px solid #e7e5e4",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(value: number) => [value, "Utilisateurs"]}
                />
                <Area
                  type="monotone"
                  dataKey="activeUsers"
                  stroke="#C75B39"
                  strokeWidth={2}
                  fill="url(#usersGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Metrics cards from API */}
        {metrics.length > 0 && (
          <div>
            <h2 className="mb-3 text-sm font-semibold text-stone-700">
              Metriques detaillees
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {metrics.map((m) => (
                <MetricCard key={m.name} metric={m} />
              ))}
            </div>
          </div>
        )}

        {/* Top endpoints */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-stone-700">
            Endpoints les plus sollicites
          </h2>
          <AdminDataTable
            columns={endpointColumns}
            data={topEndpoints}
            isLoading={isLoading}
            emptyMessage="Aucune donnee disponible."
          />
        </div>

        {/* Slow endpoints */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-stone-700">
            Endpoints les plus lents
          </h2>
          <AdminDataTable
            columns={endpointColumns}
            data={slowEndpoints}
            isLoading={isLoading}
            emptyMessage="Aucune donnee disponible."
          />
        </div>
      </div>
    </AdminLayout>
  );
}
